import React, { useState } from 'react';
import { Upload, FileText, Trash, Eye } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { uploadFile } from '../utils/uploadFile';
import api from '../api';

const TIPOS_DOCUMENTO = [
  { value: 'rg', label: 'RG' },
  { value: 'cpf', label: 'CPF' },
  { value: 'certificado', label: 'Certificado' },
  { value: 'comprovante_residencia', label: 'Comprovante de Residencia' },
  { value: 'outro', label: 'Outro' }
];

const DocumentUploader = ({ entityId, entityType, documents = [], onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedTipo, setSelectedTipo] = useState('rg');
  const [selectedFile, setSelectedFile] = useState(null);

  const handleUpload = async () => {
    if (!selectedFile) { alert('Selecione um arquivo'); return; }
    setUploading(true);
    try {
      const url = await uploadFile(selectedFile, `documentos/${entityType}/${entityId}`, (p) => setProgress(p));

      await api.post(`/${entityType}/${entityId}/documentos?tipo=${selectedTipo}&url=${encodeURIComponent(url)}&nome_arquivo=${encodeURIComponent(selectedFile.name)}`);

      setSelectedFile(null);
      setProgress(0);
      if (onUpdate) onUpdate();
    } catch (error) {
      alert('Erro ao enviar documento: ' + (error.response?.data?.detail || error.message));
    } finally { setUploading(false); }
  };

  const handleDelete = async (docUrl) => {
    if (!window.confirm('Remover este documento?')) return;
    try {
      await api.delete(`/${entityType}/${entityId}/documentos?url=${encodeURIComponent(docUrl)}`);
      if (onUpdate) onUpdate();
    } catch (error) {
      alert('Erro ao remover: ' + (error.response?.data?.detail || error.message));
    }
  };

  const docs = Array.isArray(documents) ? documents.filter(d => typeof d === 'object' && d.url) : [];

  return (
    <div className="space-y-4">
      <h4 className="text-[#FFB800] font-semibold text-sm">Documentos</h4>

      {/* Upload form */}
      <div className="flex items-end gap-3">
        <div className="w-40">
          <Label className="text-xs">Tipo</Label>
          <Select value={selectedTipo} onValueChange={setSelectedTipo}>
            <SelectTrigger className="bg-[#050505] border-[#27272A] text-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#27272A] text-white">
              {TIPOS_DOCUMENTO.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs">Arquivo</Label>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setSelectedFile(e.target.files[0])}
            className="w-full px-3 py-1.5 bg-[#050505] border border-[#27272A] rounded-sm text-white text-sm"
            data-testid="document-file-input"
          />
        </div>
        <Button
          onClick={handleUpload}
          disabled={uploading || !selectedFile}
          className="bg-[#FFB800] text-black hover:bg-[#E6A600] text-sm"
          data-testid="upload-document-button"
        >
          {uploading ? `${progress}%` : <><Upload size={14} className="mr-1" />Enviar</>}
        </Button>
      </div>

      {/* Documents list */}
      {docs.length > 0 ? (
        <div className="space-y-2">
          {docs.map((doc, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-[#050505] border border-[#27272A] rounded-sm" data-testid={`document-row-${idx}`}>
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-[#FFB800]" />
                <div>
                  <div className="text-white text-sm">{doc.nome_arquivo || 'Documento'}</div>
                  <div className="text-zinc-500 text-xs capitalize">{doc.tipo || 'outro'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:text-[#E6A600]" data-testid={`view-doc-${idx}`}>
                  <Eye size={16} />
                </a>
                <button onClick={() => handleDelete(doc.url)} className="text-zinc-400 hover:text-red-500" data-testid={`delete-doc-${idx}`}>
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-zinc-500 text-xs text-center py-2">Nenhum documento anexado</div>
      )}
    </div>
  );
};

export default DocumentUploader;
